import { useEffect, useMemo, useState } from 'preact/hooks'
import { useAthosContext } from 'libs/athos/fetchers/provider'
import { AthosCollection } from 'libs/athos/fetchers/collection'
import { AthosSearch } from 'libs/athos/fetchers/search'
import { normaliseResults, requiresPriceLocalisation } from 'libs/athos/helpers/normalisers'
import { isPriceFilterField } from 'libs/athos/components/collection/filters/FilterUtils'

const resolvedBounds = new Map()

/**
 * Resolve real market prices for the outer bounds of a price facet.
 *
 * Only products that have actually loaded carry a known market price, so on a long
 * collection the cheapest and dearest items sit pages away and the slider ends up
 * labelled from whichever prices happen to be on screen. Asking the index for
 * exactly those two items — one result each, sorted by price — and running them
 * through the usual normalisation records their real prices, so the bounds name
 * actual products rather than a scaled base value.
 *
 * Re-renders the caller once the prices land. Results are discarded: the prices are
 * read back out of the market price lookup the normalisation writes to.
 */
export function usePriceBounds(field) {
  const { results, options } = useAthosContext()
  const [, setRecorded] = useState(0)

  const sortField = useMemo(() => {
    const sortOptions = results?.sorting?.options ?? []
    return sortOptions.find((option) => isPriceFilterField(option.field))?.field ?? null
  }, [results?.sorting])

  const handle = options?.get('handle')
  const query = options?.get('query')

  useEffect(() => {
    if (!isPriceFilterField(field) || !sortField) return
    if (!handle && !query) return
    if (!requiresPriceLocalisation()) return

    let active = true

    recordExtremePrices({ sortField, handle, query }).then(() => {
      if (active) setRecorded((count) => count + 1)
    })

    return () => {
      active = false
    }
  }, [field, sortField, handle, query])
}

/** One lookup per context, however many slider instances ask for it. */
function recordExtremePrices({ sortField, handle, query }) {
  const key = [handle ?? query, sortField].join(':')

  if (!resolvedBounds.has(key)) {
    resolvedBounds.set(key, fetchExtremePrices({ sortField, handle, query }))
  }

  return resolvedBounds.get(key)
}

async function fetchExtremePrices({ sortField, handle, query }) {
  await Promise.all(['asc', 'desc'].map(async (direction) => {
    try {
      const fetcher = handle ? new AthosCollection() : new AthosSearch()
      fetcher.updateParams({ handle, query, resultsPerPage: 1, sort: `${sortField}:${direction}` })

      // An auxiliary lookup, not a shopper action — keep it out of Athos analytics.
      fetcher.params.set('beacon', 'false')

      const data = await fetcher.fetch()
      await normaliseResults(data?.results)
    } catch (error) {
      console.error(`Could not resolve the ${direction === 'asc' ? 'lowest' : 'highest'} price for the price filter:`, error)
    }
  }))
}
